import { useCallback } from "react";
import { message } from "antd";
import html2pdf from "html2pdf.js";
import { classicTemplate, corporateTemplate } from "../templates";
import type {
  InvoiceTemplateContext,
  PageSizeType,
  Templates,
} from "../templates";

type InvoiceConfig = InvoiceTemplateContext["config"];
type InvoiceOrder = InvoiceTemplateContext["order"];

interface UseInvoiceDocumentParams {
  order: InvoiceOrder | null;
  config: InvoiceConfig;
  pageSize: PageSizeType;
  template: Templates;
}

const formatInvoiceAmount = (value: unknown) => {
  const numericValue = Number.parseFloat(String(value));
  return Number.isNaN(numericValue) ? "0.00" : numericValue.toFixed(2);
};

const sanitizeFileName = (value: unknown) => {
  const name = String(value || "invoice")
    .replace(/[<>:"/\\|?*\x00-\x1f]/g, "_")
    .trim();

  return name || "invoice";
};

const removeFrame = (frame: HTMLIFrameElement) => {
  if (frame.parentNode) {
    frame.parentNode.removeChild(frame);
  }
};

export const useInvoiceDocument = ({
  order,
  config,
  pageSize,
  template,
}: UseInvoiceDocumentParams) => {
  const getInvoiceHtml = useCallback(
    (isForPrint = false) => {
      if (!order) {
        return "";
      }

      const context = {
        order,
        config,
        pageSize,
        isForPrint,
        fmt: formatInvoiceAmount,
      };

      return template === "corporate"
        ? corporateTemplate(context)
        : classicTemplate(context);
    },
    [config, order, pageSize, template],
  );

  const printInvoice = useCallback(() => {
    if (!order) {
      return;
    }

    const frame = document.createElement("iframe");
    frame.style.cssText = "position:absolute;top:-9999px;left:-9999px;";
    document.body.appendChild(frame);

    const doc = frame.contentWindow?.document || frame.contentDocument;
    if (!doc) {
      removeFrame(frame);
      return;
    }

    doc.open();
    doc.write(getInvoiceHtml(true));
    doc.close();

    window.setTimeout(() => {
      frame.contentWindow?.focus();
      frame.contentWindow?.print();
      window.setTimeout(() => removeFrame(frame), 1000);
    }, 500);
  }, [getInvoiceHtml, order]);

  const downloadInvoice = useCallback(() => {
    if (!order) {
      return;
    }

    const container = document.createElement("div");
    container.innerHTML = getInvoiceHtml(true);

    html2pdf()
      .from(container)
      .set({
        filename: `Invoice_${sanitizeFileName(order.order_no)}.pdf`,
        margin: 10,
        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation: "portrait",
        },
      })
      .save();

    message.success("Invoice PDF downloaded!");
  }, [getInvoiceHtml, order]);

  return {
    getInvoiceHtml,
    printInvoice,
    downloadInvoice,
  };
};
